import { FC, useEffect, useRef, useState } from 'react'
import { Box, Button, Paper, TextField, Typography } from '@mui/material'

import AuthService from '@services/AuthService'

/**
 * Тестовая страница для проверки механизма передачи токена через postMessage
 * Эмулирует родительское приложение которое отправляет токен в iframe
 */
export const IframeTokenTest: FC = () => {
  const [iframeUrl, setIframeUrl] = useState(
    window.location.origin +
      '/iframeForm.html?formCode=IFRAME_ALERT_FORM&objectCode=TEST_OBJECT&objectId=123&event=create'
  )
  const [logs, setLogs] = useState<string[]>([])
  const [ignoredCount, setIgnoredCount] = useState(0)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev])
    console.log(`[IframeTokenTest] ${message}`)
  }

  useEffect(() => {
    addLog('🚀 Test page initialized')
    addLog(`📍 Current user: ${AuthService.getUsername() || 'not logged in'}`)
    addLog(`🔑 Has token: ${!!AuthService.getToken()}`)

    // Слушаем запросы токена от iframe
    const handleMessage = (event: MessageEvent) => {
      // Фильтруем только релевантные сообщения
      const relevantTypes = [
        'WISKEY_KEYCLOAK_TOKEN_REQUEST',
        'WISKEY_KEYCLOAK_TOKEN_RESPONSE',
        'WISKEY_KEYCLOAK_TOKEN_REFRESH',
        'REQUEST_TOKEN',
        'KEYCLOAK_TOKEN',
        'AUTH_TOKEN',
      ]

      const messageType = event.data?.type

      // Логируем только релевантные сообщения
      if (relevantTypes.includes(messageType)) {
        addLog(`📨 Message received from iframe:`)
        addLog(`   Origin: ${event.origin}`)
        addLog(`   Type: ${messageType}`)

        // Проверяем запрос токена
        if (messageType === 'WISKEY_KEYCLOAK_TOKEN_REQUEST' || messageType === 'REQUEST_TOKEN') {
          addLog('✅ Token request detected!')
          addLog('⏳ Sending token to iframe...')

          // Отправляем токен с задержкой для наглядности
          setTimeout(() => {
            sendTokenToIframe()
          }, 500)
        }
      } else {
        // Игнорируем остальные сообщения (webpack, devtools, etc)
        setIgnoredCount(prev => prev + 1)
      }
    }

    window.addEventListener('message', handleMessage)
    addLog('👂 Listening for messages from iframe')

    return () => {
      window.removeEventListener('message', handleMessage)
      addLog('🔇 Stopped listening for messages')
    }
  }, [])

  const sendTokenToIframe = async () => {
    if (!iframeRef.current?.contentWindow) {
      addLog('❌ Iframe not ready!')

      return
    }

    // Проверяем и обновляем токен если нужно
    const isExpired = AuthService.isTokenExpired()
    if (isExpired) {
      addLog('⚠️ Token expired, refreshing...')
      try {
        await AuthService.updateToken()
        addLog('✅ Token refreshed successfully')
      } catch (error) {
        addLog(`❌ Failed to refresh token: ${error}`)
        addLog('❌ Please reload the page and login again')

        return
      }
    }

    const token = AuthService.getToken()
    const tokenParsed = (AuthService as any)._kc?.tokenParsed
    const refreshToken = (AuthService as any)._kc?.refreshToken

    if (!token) {
      addLog('❌ No token available! Please login first.')

      return
    }

    try {
      const message = {
        type: 'WISKEY_KEYCLOAK_TOKEN_RESPONSE',
        token,
        refreshToken,
        tokenParsed,
        timestamp: Date.now(),
      }

      iframeRef.current.contentWindow.postMessage(message, window.location.origin)

      const expiresIn = tokenParsed?.exp
        ? Math.floor((tokenParsed.exp * 1000 - Date.now()) / 1000)
        : 'unknown'

      addLog('✅ Token sent to iframe!')
      addLog(`   Message type: ${message.type}`)
      addLog(`   Token length: ${token.length}`)
      addLog(`   Username: ${tokenParsed?.preferred_username || 'unknown'}`)
      addLog(`   Has refresh token: ${!!refreshToken}`)
      addLog(`   Expires in: ${expiresIn}s`)
    } catch (error) {
      addLog(`❌ Error sending token: ${error}`)
    }
  }

  const handleIframeLoad = () => {
    addLog('📦 Iframe loaded')
    addLog('⏳ Waiting for token request from iframe...')

    // Автоматически отправляем токен через 2 секунды если iframe не запросил
    setTimeout(() => {
      addLog('⏰ Auto-sending token (timeout)')
      sendTokenToIframe()
    }, 2000)
  }

  const clearLogs = () => {
    setLogs([])
    setIgnoredCount(0)
    addLog('🧹 Logs cleared')
  }

  return (
    <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Typography gutterBottom variant='h4'>
        🧪 Тест механизма передачи токена через postMessage
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography gutterBottom variant='h6'>
          Информация о текущей сессии:
        </Typography>
        <Typography variant='body2'>
          👤 Username: <strong>{AuthService.getUsername() || 'Not logged in'}</strong>
        </Typography>
        <Typography variant='body2'>
          🔑 Has Token: <strong>{AuthService.getToken() ? 'Yes' : 'No'}</strong>
        </Typography>
        <Typography variant='body2'>
          🔒 Token Expired:{' '}
          <strong>{AuthService.getToken() ? String(AuthService.isTokenExpired()) : 'N/A'}</strong>
        </Typography>
        <Typography variant='body2'>
          📍 Is in iframe: <strong>{window !== window.top ? 'Yes' : 'No'}</strong>
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography gutterBottom variant='h6'>
          Настройки:
        </Typography>
        <TextField
          fullWidth
          label='Iframe URL'
          size='small'
          sx={{ mb: 1 }}
          value={iframeUrl}
          onChange={e => setIframeUrl(e.target.value)}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size='small' variant='contained' onClick={sendTokenToIframe}>
            📤 Отправить токен вручную
          </Button>
          <Button size='small' variant='outlined' onClick={clearLogs}>
            🧹 Очистить логи
          </Button>
          <Button
            color='warning'
            size='small'
            variant='outlined'
            onClick={() => window.location.reload()}
          >
            🔄 Перезагрузить
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Iframe */}
        <Paper sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1, bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant='subtitle1'>📱 Iframe Preview</Typography>
          </Box>
          <Box sx={{ flex: 1, position: 'relative' }}>
            <iframe
              ref={iframeRef}
              src={iframeUrl}
              title='Test Iframe'
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              onLoad={handleIframeLoad}
            />
          </Box>
        </Paper>

        {/* Logs */}
        <Paper sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1, bgcolor: 'success.main', color: 'white' }}>
            <Typography variant='subtitle1'>
              📋 Event Logs {ignoredCount > 0 && `(${ignoredCount} ignored)`}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 1,
              bgcolor: '#1e1e1e',
              fontFamily: 'monospace',
              fontSize: '12px',
            }}
          >
            {logs.length === 0 ? (
              <Typography color='text.secondary' sx={{ fontFamily: 'monospace' }}>
                Waiting for events...
              </Typography>
            ) : (
              logs.map((log, index) => (
                <Box
                  key={index}
                  sx={{
                    color: log.includes('❌')
                      ? '#ff6b6b'
                      : log.includes('✅')
                      ? '#51cf66'
                      : log.includes('⏳')
                      ? '#ffd43b'
                      : '#ffffff',
                    mb: 0.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {log}
                </Box>
              ))
            )}
          </Box>
        </Paper>
      </Box>

      <Paper sx={{ p: 1, mt: 2 }}>
        <Typography color='text.secondary' variant='caption'>
          💡 Tip: Откройте консоль браузера (F12) чтобы увидеть детальные логи от AuthService и
          IFrameFormWrapper
        </Typography>
      </Paper>
    </Box>
  )
}
